import React, { useEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import logoMedical from "../../assets/logo-medical.png";
import reset from "../../assets/reset.png";
import { getTexts } from "../../api/text";
import { SERVER_URL } from "../../api";
import { ROUTES } from "../../utils/constants";
import { getScenarioByScreen, handleNavigate } from "../../utils/helpers";
import { modalGenericSmall } from "../../utils/logger";

function buildAvatarUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function parseConversationMessages(value) {
	if (!value || typeof value !== "string") return null;
	try {
		const arr = JSON.parse(value);
		return Array.isArray(arr) && arr.length > 0
			? arr.filter((m) => m && (m.speaker === "doctor" || m.speaker === "doctora" || m.speaker === "patient" || m.speaker === "prompt"))
			: null;
	} catch {
		return null;
	}
}

const DEFAULT_EXPLANATION_HTML = `<div class="text-center"><div class="txt-black txt16">El <span class="bold">documento de consenso GLOSEN 2023</span> propone una serie de objetivos terapéuticos que se deben alcanzar a los 2-3 meses del inicio del tratamiento para la nefritis lúpica, tanto en primera línea de tratamiento como en recaída.</div><div class="mt-3 txt-turquoise bold txt18">¿Conoces estos objetivos?</div></div>`;

function Scenario5Conversation({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const prevState = useLocation();
	const group = useSelector((state) => state.group);

	const [messages, setMessages] = useState(null);
	const [avatarDoctorUrl, setAvatarDoctorUrl] = useState(null);
	const [avatarDoctoraUrl, setAvatarDoctoraUrl] = useState(null);
	const [avatarPatientUrl, setAvatarPatientUrl] = useState(null);
	const [avatarsReady, setAvatarsReady] = useState(false);
	const [explanationHtml, setExplanationHtml] = useState(DEFAULT_EXPLANATION_HTML);
	const lastMessageRef = useRef(null);

	useEffect(() => {
		const gameId = group?.game;
		if (!gameId) return;
		setAvatarsReady(false);
		getTexts({ game: gameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});
					const parsed = parseConversationMessages(map["scenario5.conversation.messages"]);
					setMessages(parsed);
					setAvatarDoctorUrl(buildAvatarUrl(map["avatar.doctor"]));
					setAvatarDoctoraUrl(buildAvatarUrl(map["avatar.doctora"]));
					setAvatarPatientUrl(buildAvatarUrl(map["avatar.patient"]));
					if (map["scenario5.conversation.explanationModal"] != null)
						setExplanationHtml(map["scenario5.conversation.explanationModal"]);
				}
				setAvatarsReady(true);
			})
			.catch(() => setAvatarsReady(true));
	}, [group?.game]);

	const scrollToBottom = () => {
		lastMessageRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	const renderChatContent = () => {
		if (!avatarsReady) {
			return (
				<div className="d-flex align-items-center justify-content-center py-5">
					<div className="txtWhite">Cargando conversación...</div>
				</div>
			);
		}
		if (messages && messages.length > 0) {
			return (
				<div className="d-flex flex-column conversation-chat">
					{messages.map((msg, index) => (
						<div
							key={index}
							className={`conversation-message ${msg.speaker}`}
							ref={index === messages.length - 1 ? lastMessageRef : null}
						>
							{msg.speaker === "prompt" ? (
								<div className="conversation-prompt">
									{msg.content && msg.content.indexOf("<") >= 0 ? (
										<span dangerouslySetInnerHTML={{ __html: msg.content }} />
									) : (
										msg.content
									)}
								</div>
							) : msg.speaker === "doctor" || msg.speaker === "doctora" ? (
								<>
									<div className="conversation-avatar">
										{((msg.speaker === "doctora" ? avatarDoctoraUrl : avatarDoctorUrl)) ? (
											<img
												src={(msg.speaker === "doctora" ? avatarDoctoraUrl : null) || avatarDoctorUrl}
												alt={msg.speaker === "doctora" ? "Doctora" : "Doctor"}
												style={{ width: "100%", height: "100%", objectFit: "cover" }}
											/>
										) : (
											<div style={{ width: "100%", height: "100%", background: "#e0e8ec", borderRadius: "50%" }} aria-hidden />
										)}
									</div>
									<div className="conversation-bubble">
										{msg.content && msg.content.indexOf("<") >= 0 ? (
											<span dangerouslySetInnerHTML={{ __html: msg.content }} />
										) : (
											msg.content
										)}
									</div>
								</>
							) : (
								<>
									<div className="conversation-bubble">
										{msg.content && msg.content.indexOf("<") >= 0 ? (
											<span dangerouslySetInnerHTML={{ __html: msg.content }} />
										) : (
											msg.content
										)}
									</div>
									<div className="conversation-avatar">
										{avatarPatientUrl ? (
											<img
												src={avatarPatientUrl}
												alt="Paciente"
												style={{ width: "100%", height: "100%", objectFit: "cover" }}
											/>
										) : (
											<div style={{ width: "100%", height: "100%", background: "#e8e8a0", borderRadius: "50%" }} aria-hidden />
										)}
									</div>
								</>
							)}
						</div>
					))}
				</div>
			);
		}
		// Sin mensajes desde API: placeholders en lugar de imágenes por defecto
		return (
			<div className="d-flex flex-column conversation-chat">
				{[0, 1, 2, 3, 4].map((index) => (
					<div
						key={index}
						className={index % 2 === 0 ? "d-flex justify-content-start" : "d-flex justify-content-end"}
						ref={index === 4 ? lastMessageRef : null}
					>
						<div
							style={{
								maxWidth: index % 2 === 0 ? 607 : 538,
								height: 80,
								background: "#e0e0e0",
								borderRadius: 12,
								width: "80%",
							}}
							aria-hidden
						/>
					</div>
				))}
			</div>
		);
	};

	return (
		<div className="h-100">
			<div className="container-fluid bg-internas">
				<div style={{ paddingTop: "6vh", paddingLeft: "6vh" }}>
					<img width={240} src={logoMedical} alt="" />
				</div>
				<div className="row d-flex justify-content-center align-items-center mt-5">
					<div
						className="col-9"
						style={{ height: "60vh", overflow: "scroll" }}
					>
						{renderChatContent()}
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "3vh",
					bottom: "6vh",
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main-cancel me-3"
						onClick={(e) => scrollToBottom()}
					>
						<img height={"40%"} src={reset} alt="" />
						<div className="text-btn-main ms-2">
							Ver conversación
						</div>
					</div>
					<div
						className="btn-main"
						onClick={(e) => {
							modalGenericSmall({ html: explanationHtml || DEFAULT_EXPLANATION_HTML }).then((res) => {
								if (res.isConfirmed)
									handleNavigate({
										group: group,
										navigate: navigate,
										dispatch: dispatch,
										navigateTo: ROUTES.SCENARIO5_EXPLANATION,
										scenarioNum: getScenarioByScreen(ROUTES.SCENARIO5_EXPLANATION),
									});
							});
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default injectIntl(Scenario5Conversation);
